// Access-code auth — the app is gated by invite codes stored in the
// Firestore "access_codes" collection (managed alongside the landing page's
// referral data in the blinkbooks-marketing project).
//
// Document shape (doc id = the code itself, uppercase):
//   { code, label, active, maxUses, uses, createdAt, lastUsedAt }
import { doc, getDoc, increment, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "@/integrations/firebase/client";

const STORAGE_KEY = "bb_access";
const COLLECTION = "access_codes";

export const normalizeCode = (raw: string) => raw.trim().toUpperCase().replace(/\s+/g, "");

export interface CodeCheck {
  ok: boolean;
  reason?: "empty" | "not_found" | "inactive" | "exhausted" | "error";
}

export async function validateAccessCode(raw: string): Promise<CodeCheck> {
  const code = normalizeCode(raw);
  if (!code) return { ok: false, reason: "empty" };
  try {
    const snap = await getDoc(doc(db, COLLECTION, code));
    if (!snap.exists()) return { ok: false, reason: "not_found" };
    const d = snap.data() as { active?: boolean; maxUses?: number | null; uses?: number };
    if (d.active === false) return { ok: false, reason: "inactive" };
    if (d.maxUses != null && (d.uses ?? 0) >= d.maxUses) return { ok: false, reason: "exhausted" };

    // Best-effort usage tracking — access is already granted; rules may deny writes.
    updateDoc(snap.ref, { uses: increment(1), lastUsedAt: serverTimestamp() }).catch(() => {});

    localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, grantedAt: Date.now() }));
    return { ok: true };
  } catch {
    return { ok: false, reason: "error" };
  }
}

export const hasAccess = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return !!raw && !!JSON.parse(raw).code;
  } catch {
    return false;
  }
};

export const getStoredAccessCode = (): string | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw).code as string) ?? null : null;
  } catch {
    return null;
  }
};

export const clearAccess = () => localStorage.removeItem(STORAGE_KEY);
